import { Text } from "@twinkltech/web-ui";
import React, { ElementType, ComponentPropsWithoutRef } from "react";
import { useId } from "react";

export type TagVariant =
  | "dark"
  | "green"
  | "green-alt"
  | "blue"
  | "light-blue"
  | "light-blue-alt"
  | "orange"
  | "pink"
  | "default"
  | "default-alt";

type TagProps<E extends ElementType = "span"> = {
  as?: E;
  asTrigger?: boolean;
  variant?: TagVariant;
  children?: React.ReactNode;
  icon?: React.ReactNode | null;
  className?: string;
  disabled?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
} & Omit<
  ComponentPropsWithoutRef<E>,
  "as" | "asTrigger" | "variant" | "className"
>;

export const StyledTag = <E extends ElementType = "span">({
  as,
  children,
  className,
  disabled,
  icon = null,
  size = "md",
  variant = "default",
  ...props
}: TagProps<E>) => {
  const Component = as || "span";
  const labelId = useId();

  // For some reason, xs isn't available in the text component, so we leave it to the CSS to handle
  const textSize =
    size === "xs" ? null : size === "sm" ? "sm-normal" : "base-normal";

  return (
    <Component
      aria-labelledby={labelId}
      className={[
        "inline-flex items-center gap-050 border whitespace-nowrap ",
        size === "xs" && "px-075 py-025 text-xs rounded-[8px]",
        size === "sm" && "px-2 py-1 text-sm rounded-[8px]",
        size === "md" && "px-150 py-050 text-base rounded-lg",
        size === "lg" && "px-200 py-075 text-lg rounded-lg",
        variant === "default" &&
          "bg-neutral-100 border-neutral-150 text-blue-800",
        variant === "default-alt" &&
          "bg-neutral-100 border-neutral-300 text-blue-800",
        variant === "green" && "bg-green-50 border-green-100",
        variant === "green" && "bg-green-50 border-green-500 text-blue-850",
        variant === "blue" && "bg-blue-100 border-blue-300",
        variant === "light-blue" && "bg-blue-50 border-blue-700 text-blue-700",
        variant === "light-blue-alt" &&
          "bg-blue-50 border-blue-100 text-blue-800",
        variant === "orange" && "bg-orange-100 border-neutral-300 text-white",
        variant === "pink" && "bg-pink-50 border-pink-700 text-pink-700",
        variant === "dark" && "bg-neutral-700 border-neutral-600 text-white",

        disabled ? "opacity-50 !cursor-not-allowed" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      role={as ? undefined : "note"}
      {...props}
    >
      <Text
        as="span"
        className="flex items-center"
        id={labelId}
        variant={textSize}
      >
        {children}
      </Text>
      {icon}
    </Component>
  );
};
